import { Layout } from '@/components/Layout';
import { MoreEpisodes } from '@/components/MoreEpisodes';
import { PageMeta } from '@/components/PageMeta';
import { SchemaMarkup } from '@/components/SchemaMarkup';
import { ShowHero } from '@/components/ShowHero';
import { VideoBaseContent } from '@/components/VideoBaseContent';
import { Content } from '@/types/content';
import { getCurrentBrand } from '@/utils/getCurrentBrand';
import { getDomain } from '@/utils/getDomain';
import { HOUR } from '@/utils/ttl';
import { withCacheControlHeaders } from '@/utils/withCacheControlHeaders';
import { VIDEO_PAGES_CONFIG } from 'base/configs/video';
import { VideoShowPlaylistPage, getShowPlaylistPageData } from 'lib/data/video';
import { omitBy } from 'lodash-es';
import { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import { isUndefined } from 'typesafe-utils';

const domain = getDomain();
const brand = getCurrentBrand();

export async function getServerSideProps({ params, resolvedUrl, res }: GetServerSidePropsContext) {
  const searchQueriesString = new URL(resolvedUrl, domain).searchParams.toString();
  const addUrlQuery = (url: string) => (searchQueriesString ? `${url}/?${searchQueriesString}` : url);
  const pickIdx = (params?.id?.length || 1) - 1;
  const playlistId = params?.id?.[pickIdx];

  if (!playlistId) {
    return {
      notFound: true,
    };
  }

  const data = await getShowPlaylistPageData(playlistId);

  if (!data) {
    return {
      notFound: true,
    };
  }

  const { show, relatedMedia, tagsList } = data;

  const relatedMediaItems = relatedMedia.map((item, index) => ({
    ...item,
    url: addUrlQuery(item.url),
  }));

  withCacheControlHeaders(res, 1 * HOUR);

  return { props: { show, relatedMedia: relatedMediaItems, tagsList } };
}

export default function SingleShowPage({
  show,
  relatedMedia,
  tagsList,
}: { show: VideoShowPlaylistPage['show'] } & Omit<VideoShowPlaylistPage, 'show'>) {
  const { asPath } = useRouter();

  const firstEpisodeUrl = relatedMedia[0]?.url;

  const pageData: Content = {
    meta: {},
    type: 'videoWebPage',
    children: [],
    data: omitBy(
      {
        brand: brand.name,
        titleSuffix: ` | Video | ${brand.name}`,
        seoTitle: show.title,
        domain: brand.host,
        canonical: `${getDomain()}${asPath.replace('/', '')}`,
        keywords: `video, playlist, ${brand.name}`,
        seoDescription: show.description || VIDEO_PAGES_CONFIG.showsPage?.getShowSeoDescription?.(show.title),
        ogImageUrl: show.image,
        mainEntityId: getDomain(false) + firstEpisodeUrl,
      },
      isUndefined,
    ),
  };

  const prominentTag = relatedMedia[0]?.prominentTag;

  return (
    <Layout
      content={
        <>
          <PageMeta pageData={pageData} />
          <SchemaMarkup pageData={pageData} />

          <VideoBaseContent
            label="Populärt:"
            tagsList={tagsList}
            breadcrumbs={[
              { label: 'Video start', url: '/video' },
              { label: 'Shows', url: '/video/show' },
              { label: show.title, url: '' },
            ]}
          >
            <ShowHero
              headline={show.title}
              image={{ src: show.image, alt: show.title }}
              description={show.description || VIDEO_PAGES_CONFIG.showsPage?.getShowDefaultDescription?.(show.title)}
              share="Dela"
              category={{
                value: prominentTag?.toLowerCase(),
                url: prominentTag && `/video/tag/${prominentTag}`,
              }}
              button={{ value: 'Spela första avsnittet', url: firstEpisodeUrl }}
            />
            {relatedMedia && <MoreEpisodes episodes={relatedMedia} headline="Fler avsnitt" />}
          </VideoBaseContent>
        </>
      }
    />
  );
}
